import { useCallback, useEffect, useState } from 'react';
import type { Ctx } from '../ctx';
import { authFetch } from '../api';
import { LOST_LAG_CRITICAL_MIN, fmt, maskEmail } from '../model';
import { Modal } from '../ui';
import type { LostItem, LostResponse } from '../types';

type Pending = { item: LostItem; kind: 'recover' | 'ack' };

function ResolutionPill({ item }: { item: LostItem }) {
  if (!item.resolution) return <span className="chip warn">open</span>;
  const cls = item.resolution === 'acked' ? '' : 'ok';
  return (
    <span className={cls ? `chip ${cls}` : 'chip'} title={item.resolvedBy ? `by ${item.resolvedBy}` : undefined}>
      {item.resolution}
    </span>
  );
}

/** TTS only: Webflow-recorded submissions that never reached the service, with Recover / Acknowledge. */
export default function LostPanel({ ctx }: { ctx: Ctx }) {
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState<LostResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await authFetch<LostResponse>(`/api/lost?status=${showAll ? 'all' : 'open'}`));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [showAll]);

  useEffect(() => {
    void load();
  }, [load]);

  const dryRun = ctx.data.dials?.tts.dryRun !== false;
  const cursor = data?.cursor ?? null;
  const critical = !!cursor && cursor.lagMin > LOST_LAG_CRITICAL_MIN;
  const items = (data?.items ?? []).filter((i) => showAll || !i.resolution);

  const run = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    setErr(null);
    const id = encodeURIComponent(pending.item.submissionId);
    try {
      if (pending.kind === 'recover') {
        const r = await authFetch<{ ok: boolean; status: string }>(`/api/lost/${id}/recover`, { method: 'POST' });
        ctx.toast(`Recover ${pending.item.submissionId} → ${r.ok ? 'ok' : 'failed'}: ${r.status}`);
      } else {
        const r = await authFetch<{ ok: boolean }>(`/api/lost/${id}/ack`, { method: 'POST' });
        ctx.toast(`Acknowledge ${pending.item.submissionId} → ${r.ok ? 'ok' : 'failed'}`);
      }
      setPending(null);
      await load();
      ctx.data.reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card pad" style={{ marginTop: 14 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>Lost submissions</h2>
        <div className="seg" role="group" aria-label="lost filter">
          <button type="button" className={showAll ? '' : 'on'} onClick={() => setShowAll(false)}>
            Open
          </button>
          <button type="button" className={showAll ? 'on' : ''} onClick={() => setShowAll(true)}>
            All
          </button>
        </div>
      </div>

      {error ? (
        <div className="notice" style={{ margin: '8px 0' }}>
          <strong>could not load:</strong> {error}
        </div>
      ) : data && !cursor ? (
        <p className="note" style={{ margin: '8px 0' }}>Lost-submission check is off</p>
      ) : cursor ? (
        <div
          className={critical ? 'notice' : 'note'}
          role={critical ? 'alert' : undefined}
          style={{ margin: '8px 0', ...(critical ? { borderLeftColor: 'var(--bad)', background: 'var(--bad-soft)' } : {}) }}
        >
          {critical ? <strong>Scan stalled — </strong> : null}
          Scanned up to <code>{fmt(cursor.position)}</code> · checked up to {Math.round(cursor.lagMin)} min ago
          {critical ? ` (over ${LOST_LAG_CRITICAL_MIN} min: the check itself is behind)` : ''}
        </div>
      ) : null}

      <div className="tw">
        <table>
          <thead>
            <tr>
              <th>Submitted (UTC)</th>
              <th>Email</th>
              <th>Type</th>
              <th>Page</th>
              <th>Detected</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const open = !i.resolution;
              const lock = !ctx.admin ? 'admin only' : undefined;
              return (
                <tr key={i.submissionId}>
                  <td className="mono dim">{fmt(i.submittedAt)}</td>
                  <td>{maskEmail(i.email)}</td>
                  <td>
                    <span className="chip">{i.requestType}</span>
                  </td>
                  <td className="mono" title={i.displayName}>{i.publishedPath}</td>
                  <td className="mono dim">{fmt(i.detectedAt)}</td>
                  <td>
                    <ResolutionPill item={i} />
                  </td>
                  <td>
                    {open ? (
                      <span className="row">
                        <button
                          type="button"
                          className="btn sm primary"
                          disabled={!ctx.admin}
                          title={lock}
                          onClick={() => { setErr(null); setPending({ item: i, kind: 'recover' }); }}
                        >
                          Recover
                        </button>
                        <button
                          type="button"
                          className="btn sm"
                          disabled={!ctx.admin}
                          title={lock}
                          onClick={() => { setErr(null); setPending({ item: i, kind: 'ack' }); }}
                        >
                          Acknowledge
                        </button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {!items.length && data ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty">
                    No lost submissions. Every Talk to Sales submission Webflow recorded reached the service.
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="note" style={{ margin: '8px 0 0' }}>
        Recover runs intake for the submission under the current dial
        {dryRun ? ': TTS is dry-run, so recover is a dry-run too — nothing is written to HubSpot / Customer.io' : ' (LIVE — it writes to HubSpot / Customer.io)'}.
      </p>

      {pending ? (
        <Modal
          title={pending.kind === 'recover' ? 'Recover this submission?' : 'Acknowledge this submission?'}
          onClose={() => !busy && setPending(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setPending(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void run()} disabled={busy}>
                {busy ? 'Working…' : pending.kind === 'recover' ? 'Recover' : 'Acknowledge'}
              </button>
            </>
          }
        >
          <p className="note" style={{ margin: '0 0 8px' }}>
            {pending.kind === 'recover' ? (
              dryRun ? (
                <>Runs intake for <code>{pending.item.submissionId}</code>. TTS is dry-run, so this is a dry-run too.</>
              ) : (
                <>Runs intake for <code>{pending.item.submissionId}</code> LIVE: it writes to HubSpot / Customer.io.</>
              )
            ) : (
              <>Marks <code>{pending.item.submissionId}</code> as handled without running intake.</>
            )}
          </p>
          {err ? (
            <div className="notice">
              <strong>error:</strong> <code>{err}</code>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </section>
  );
}
